// The product page's quick shop for its "Complete the set" and "Also in" rows (Jasen, Oct 21): the homepage's dialog
// (snippets/stally-rd-quick-dialog.liquid) filled from #pd-quick-data, adding to the store's cart (Ajax Cart API). The
// page has no bag drawer, so it ends as the page's own Add to bag does: "Added to your rotation. View bag".
// shortcut: the render, size, status and add steps repeat stally-rd-home.js's quick controller; unify them once
// home.js can change after the Oct 23 launch.

const get = id => document.getElementById(id);
const cartUrl = path => `${window.Shopify?.routes?.root || '/'}${path}`;
const currency = window.Shopify?.currency?.active || 'USD';
const moneyFormat = options => {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'narrowSymbol', ...options }); }
  catch { return new Intl.NumberFormat('en-US', { style: 'currency', currency, ...options }); } // Safari before 14.1
};
const wholeDollars = moneyFormat({ minimumFractionDigits: 0, maximumFractionDigits: 0 });
const withCents = moneyFormat({});
const amount = cents => (cents % 100 === 0 ? wholeDollars : withCents).format(cents / 100);
const escapeHTML = value => String(value).replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

// as on the homepage: a lone one-size choice in stock is chosen for the shopper
const defaultSize = color => (color.sizes.length === 1 && /^(?:one[ -]?size|os|osfa|adjustable)$/i.test(color.sizes[0].name) && color.sizes[0].available ? color.sizes[0].name : null);
const productHref = (color, sizeName = null) => {
  const size = color.sizes.find(choice => choice.name === sizeName && choice.available) || color.sizes.find(choice => choice.available) || color.sizes[0];
  return Number.isSafeInteger(size?.id) ? `${color.url}?variant=${size.id}` : color.url;
};

let quick = null;

function pieces() {
  try {
    const data = JSON.parse(get('pd-quick-data')?.textContent || '[]');
    return Array.isArray(data) ? data.filter(piece => piece && typeof piece.id === 'string' && Array.isArray(piece.colors) && piece.colors.length) : [];
  } catch {
    return [];
  }
}

function openDialog(dialog) {
  for (const nav of document.querySelectorAll('.rd .desktop-nav > details[open]')) {
    nav.querySelector('summary').focus();
    nav.open = false;
  }
  document.querySelectorAll('.rd dialog[open]').forEach(open => { if (open !== dialog) open.close(); });
  if (!dialog.open) dialog.showModal();
}

function renderSelection() {
  const color = quick.product.colors[quick.color];
  const image = get('quick-image');
  image.src = color.image || '';
  image.alt = `${quick.product.name} in ${color.name}`;
  image.dataset.productType = quick.product.type;
  // the photo shows whole at its own shape, so its size comes before it loads (no jump when the color changes)
  image.width = color.imageWidth || 1200;
  image.height = color.imageHeight || 1500;
  get('quick-color-name').textContent = color.name;
  get('quick-swatches').querySelectorAll('.swatch').forEach((button, index) => button.setAttribute('aria-pressed', String(index === quick.color)));
  get('quick-sizes').innerHTML = color.sizes.map((size, index) => `<button type="button" class="size-choice" data-size-choice="${index}" aria-pressed="${size.name === quick.size}" aria-label="${escapeHTML(size.name)}${size.available ? '' : ', sold out'}"${size.available ? '' : ' disabled'}>${escapeHTML(size.name)}</button>`).join('');
  updateStatus();
}

function updateStatus(message) {
  const color = quick.product.colors[quick.color];
  const available = color.sizes.some(size => size.available);
  const selected = color.sizes.find(size => size.name === quick.size && size.available);
  const add = get('add-to-bag');
  get('quick-sizes').querySelectorAll('[data-size-choice]').forEach(button => button.setAttribute('aria-pressed', String(color.sizes[Number(button.dataset.sizeChoice)].name === quick.size)));
  get('quick-status').textContent = message || (!available ? 'This color is currently sold out.' : selected ? `${selected.name} selected.` : 'Select a size to add this piece.');
  // not disabled while adding, so keyboard focus stays on the button (as the page's own Add to bag); the adding flag
  // blocks repeats
  add.disabled = !selected;
  if (quick.adding) add.setAttribute('aria-busy', 'true');
  else add.removeAttribute('aria-busy');
  get('quick-title-link').href = productHref(color, quick.size);
  add.textContent = !available ? 'Sold out' : selected ? (typeof color.preorder === 'string' ? 'Pre-order' : 'Add to bag') : 'Select a size';
  // a pre-order's ship line and a final-sale item's label, as on the product page; a hidden line must not describe the button
  const shipLine = get('quick-preorder');
  shipLine.textContent = color.preorder || '';
  shipLine.hidden = !color.preorder || !available;
  const finalLine = get('quick-final-sale');
  if (finalLine) finalLine.hidden = !color.finalSale;
  const described = [finalLine, shipLine].filter(line => line && !line.hidden).map(line => line.id).join(' ');
  if (described) add.setAttribute('aria-describedby', described);
  else add.removeAttribute('aria-describedby');
}

function openQuick(handle, colorName) {
  const product = pieces().find(piece => piece.id === handle);
  if (!product || !get('quick-dialog')) return;
  const color = Math.max(0, product.colors.findIndex(choice => choice.name === colorName));
  quick = { product, color, size: defaultSize(product.colors[color]), adding: false };
  get('quick-title-link').textContent = product.name;
  get('quick-price').textContent = amount(product.price);
  get('quick-fit').textContent = product.fit || '';
  get('quick-fit').hidden = !product.fit;
  get('quick-description').textContent = product.description || '';
  get('quick-swatches').innerHTML = product.colors.map((choice, index) =>
    `<button type="button" class="swatch" data-quick-color="${index}" aria-label="${escapeHTML(choice.name)}" aria-pressed="${index === color}"><span class="visually-hidden">${escapeHTML(choice.name)}</span></button>`).join('');
  get('quick-swatches').querySelectorAll('.swatch').forEach((button, index) => button.style.setProperty('--swatch-color', product.colors[index].hex));
  renderSelection();
  openDialog(get('quick-dialog'));
}

async function addToBag() {
  if (!quick || quick.adding) return;
  const color = quick.product.colors[quick.color];
  const size = color.sizes.find(choice => choice.name === quick.size && choice.available);
  if (!size) return;
  const selection = quick;
  selection.adding = true;
  updateStatus('Adding to your bag…');
  try {
    // the product page's form posts the same Pre-order property
    const response = await fetch(cartUrl('cart/add.js'), {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: size.id, quantity: 1, ...(color.preorder ? { properties: { 'Pre-order': color.preorder } } : {}) }] })
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(json.description || json.message || `Add to bag failed: ${response.status}`);
    const cart = await (await fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } })).json();
    document.dispatchEvent(new CustomEvent('stally:cart', { detail: cart }));
    selection.adding = false;
    if (quick !== selection) return;
    updateStatus();
    get('quick-status').innerHTML = `Added to your rotation. <a href="${cartUrl('cart')}">View bag</a>`;
  } catch (error) {
    selection.adding = false;
    if (quick === selection) updateStatus(error.message);
  }
}

document.addEventListener('click', event => {
  if (!(event.target instanceof Element)) return;
  const opener = event.target.closest('[data-pd-quick]');
  if (opener) {
    openQuick(opener.dataset.pdQuick, opener.dataset.pdQuickColor);
    return;
  }
  const dialog = get('quick-dialog');
  if (!quick || !dialog?.contains(event.target)) return;
  const control = event.target.closest('button');
  if (!control) return;
  if (control.hasAttribute('data-quick-color')) {
    const index = Number(control.dataset.quickColor);
    const color = quick.product.colors[index];
    if (!color) return;
    quick.color = index;
    quick.size = color.sizes.some(size => size.name === quick.size && size.available) ? quick.size : defaultSize(color);
    renderSelection();
  }
  if (control.hasAttribute('data-size-choice')) {
    const size = quick.product.colors[quick.color].sizes[Number(control.dataset.sizeChoice)];
    if (size?.available) {
      quick.size = size.name;
      updateStatus();
    }
  }
  if (control.id === 'add-to-bag') addToBag();
});
